import { useEffect, useRef } from 'react'
import { useOrg } from '@/admin/hooks/use-org'

// Persists an in-progress wizard's reducer state to localStorage so that a
// user who *reloads the page* mid-flow resumes on the same step with the same
// captured values. The cache is scoped per tenant + wizard.
//
// The draft is intentionally short-lived: it survives a hard reload (an
// EXTERNAL event) but is discarded the moment the user leaves the wizard by
// in-app navigation (cancel, a sidebar link, the browser back button - all
// INTERNAL events). See `useWizardCachePersistence` for how that
// refresh-vs-navigate distinction is drawn. The cache is also cleared once the
// form is submitted.
//
// Follows the repo localStorage convention (see use-table-column-preferences):
// `raven:<domain>:v1[:<scope>]` keys, `typeof window` guard, try/catch around
// every storage call so blocked/private-mode storage never breaks the wizard.
const KEY_PREFIX = 'raven:wizard-cache:v1'

export function buildWizardCacheKey(
  wizardName: string,
  tenantSlug: string | null,
): string | null {
  // Refuse to read or write without a tenant slug - a shared "global" key
  // would bleed one tenant's in-progress wizard into another for users with
  // memberships in multiple tenants.
  if (!tenantSlug) return null
  return `${KEY_PREFIX}:${tenantSlug}:${wizardName}`
}

export function readWizardCache<T>(key: string | null): T | null {
  if (typeof window === 'undefined' || !key) return null
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

export function writeWizardCache<T>(key: string | null, value: T): void {
  if (typeof window === 'undefined' || !key) return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Ignore storage failures so the wizard keeps working in private mode.
  }
}

export function clearWizardCache(key: string | null): void {
  if (typeof window === 'undefined' || !key) return
  try {
    window.localStorage.removeItem(key)
  } catch {
    // Ignore storage failures.
  }
}

// Compute the tenant-scoped storage key for a wizard. Call this *before* the
// wizard's `useReducer` so the key is available to a lazy initializer that
// restores from `readWizardCache`.
//
// On a hard page reload the React Query `org-context` is not persisted (it is
// in NEVER_PERSIST_KEYS), so `useOrg()` returns undefined on the first render -
// which is exactly when the reducer's lazy initializer runs. Fall back to the
// tenant slug that `useOrg` mirrors into sessionStorage (survives reloads in
// the same tab) so the key is available synchronously at mount; otherwise the
// draft would neither restore nor be re-persisted (the persistence effect would
// overwrite it with the blank initial state once org resolved).
export function useWizardCacheKey(wizardName: string): string | null {
  const { data: org } = useOrg()
  const tenantSlug =
    org?.organisation?.tenant_slug ??
    (typeof window !== 'undefined' ? sessionStorage.getItem('tenantSlug') : null)
  return buildWizardCacheKey(wizardName, tenantSlug)
}

// Persist `state` to localStorage on every change, or clear the cache once
// `done` (the form has been submitted). Pair with `useWizardCacheKey` +
// `readWizardCache` in the reducer's lazy initializer.
//
// Additionally clears the cache when the wizard unmounts. This is the
// mechanism that discards the draft on in-app navigation while keeping it
// across a reload: React runs a cleanup function when the component unmounts
// (cancel, a route change, the SPA back button), but a hard page reload tears
// down the JS context WITHOUT running React cleanups. So the unmount-clear
// fires for INTERNAL navigation only; an EXTERNAL reload leaves the cache in
// place, and the reducer's lazy initializer restores it on remount. That
// asymmetry is deliberate and is the whole contract: refresh -> resume,
// navigate away -> start fresh.
export function useWizardCachePersistence<T>(
  cacheKey: string | null,
  state: T,
  done: boolean,
): void {
  useEffect(() => {
    if (!cacheKey) return
    if (done) {
      clearWizardCache(cacheKey)
    } else {
      writeWizardCache(cacheKey, state)
    }
  }, [cacheKey, state, done])

  // Clear on unmount (in-app navigation). Keep the latest cache key in a ref
  // so the empty-deps cleanup reads the key that is current at unmount time -
  // the key can arrive late (null -> real) once the org context resolves, and
  // we must not capture the stale null from first render. The ref is synced in
  // its own effect (never during render) so it always holds the last committed
  // key by the time the unmount cleanup runs.
  const cacheKeyRef = useRef(cacheKey)
  useEffect(() => {
    cacheKeyRef.current = cacheKey
  }, [cacheKey])
  useEffect(() => {
    return () => {
      clearWizardCache(cacheKeyRef.current)
    }
  }, [])
}
